import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, Route, Routes } from 'react-router-dom'
import ZipProgressDialog from '../components/ZipProgressDialog'
import { api } from '../lib/api'
import { startZipDownloadFromWorker, ZipDownloadCancelledError, type ZipBuildProgress } from '../lib/clientZip'
import { competitionStatusLabel } from '../lib/competitionStatus'

type Competition = { id:number, slug:string, title:string, closesAt:string, status:string, zipKey?:string|null }
type AdminEntry = {
  id: number
  competitionNumber: string
  className: string
  categoryLabel: string
  title: string
  zipPath: string
  previewUrl: string
}
type AdminEntriesResp = {
  competition: { id:number, slug:string, title:string }
  entries: AdminEntry[]
}
type JudgeExportItem = { id: number, name: string, scoreCount: number }
type JudgeExportResp = {
  competition: { id: number, title: string }
  judges: JudgeExportItem[]
}
type AdminSubmission = {
  id: number
  competitionNumber: string
  className: string
  email: string
  entryCount: number
  canResetRegistration: boolean
}
type AdminSubmissionResp = {
  submission: AdminSubmission
}
type ZipDialogState = {
  title: string
  progress: ZipBuildProgress
  cancel: () => void
}

function makeSlug(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

function isValidSlug(value: string) {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value.trim())
}

function isValidCompetitionNumber(value: string) {
  return /^[NIA][0-9]+$/i.test(value.trim())
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

function toLocalDateTimeInputValue(d: Date) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hour = String(d.getHours()).padStart(2, '0')
  const minute = String(d.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hour}:${minute}`
}

function defaultCloseAtOneMonthAheadMidnight() {
  const d = new Date()
  d.setMonth(d.getMonth() + 1)
  d.setHours(0, 0, 0, 0)
  return toLocalDateTimeInputValue(d)
}

const ENTRY_CATEGORY_TABS = ['Mono', 'Colour', 'Themed'] as const
const MOBILE_WIDTH_MEDIA_QUERY = '(max-width: 760px)'

function defaultEntriesColumns() {
  if (typeof window !== 'undefined' && window.matchMedia(MOBILE_WIDTH_MEDIA_QUERY).matches) return 2
  return 3
}

function AdminHome() {
  const [me, setMe] = useState<{loggedIn:boolean, username?:string}>({loggedIn:false})
  const [err, setErr] = useState('')
  const [ok, setOk] = useState('')
  const [createdSlug, setCreatedSlug] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [comps, setComps] = useState<Competition[]>([])
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [slugIsManual, setSlugIsManual] = useState(false)
  const [closesAt, setClosesAt] = useState(defaultCloseAtOneMonthAheadMidnight)
  const [judgeDialogOpen, setJudgeDialogOpen] = useState(false)
  const [judgeName, setJudgeName] = useState('')
  const [judgeCompId, setJudgeCompId] = useState<number | null>(null)
  const [judgeExportDialogOpen, setJudgeExportDialogOpen] = useState(false)
  const [judgeExportCompId, setJudgeExportCompId] = useState<number | null>(null)
  const [judgeExportCompTitle, setJudgeExportCompTitle] = useState('')
  const [judgeExportLoading, setJudgeExportLoading] = useState(false)
  const [judgeExportJudges, setJudgeExportJudges] = useState<JudgeExportItem[]>([])
  const [selectedJudgeExportId, setSelectedJudgeExportId] = useState('')
  const [deadlineDialogOpen, setDeadlineDialogOpen] = useState(false)
  const [deadlineCompId, setDeadlineCompId] = useState<number | null>(null)
  const [deadlineCompTitle, setDeadlineCompTitle] = useState('')
  const [deadlineValue, setDeadlineValue] = useState('')
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false)
  const [registrationCompId, setRegistrationCompId] = useState<number | null>(null)
  const [registrationCompTitle, setRegistrationCompTitle] = useState('')
  const [registrationCompetitionNumber, setRegistrationCompetitionNumber] = useState('')
  const [registrationEmailValue, setRegistrationEmailValue] = useState('')
  const [registrationResult, setRegistrationResult] = useState<AdminSubmission | null>(null)
  const [resetRegistrationConfirmId, setResetRegistrationConfirmId] = useState<number | null>(null)
  const [entriesDialogOpen, setEntriesDialogOpen] = useState(false)
  const [entriesLoading, setEntriesLoading] = useState(false)
  const [entriesData, setEntriesData] = useState<AdminEntriesResp | null>(null)
  const [entriesColumns, setEntriesColumns] = useState(defaultEntriesColumns)
  const [entriesActiveClass, setEntriesActiveClass] = useState('')
  const [entriesActiveCategory, setEntriesActiveCategory] = useState('')
  const [deleteEntryConfirmId, setDeleteEntryConfirmId] = useState<number | null>(null)
  const [toast, setToast] = useState<{ message: string, type: 'success'|'error' } | null>(null)
  const [closeSubmissionsConfirmId, setCloseSubmissionsConfirmId] = useState<number | null>(null)
  const [closeJudgingConfirmId, setCloseJudgingConfirmId] = useState<number | null>(null)
  const [zipDialog, setZipDialog] = useState<ZipDialogState | null>(null)
  const toastTimerRef = useRef<number | null>(null)
  const bodyScrollYRef = useRef(0)
  const activeZipCancelRef = useRef<(() => void) | null>(null)
  const [postStateByKey, setPostStateByKey] = useState<Record<string, 'idle'|'loading'|'success'>>({})
  const postResetTimers = useRef<Record<string, number>>({})
  const confirmResetTimers = useRef<Record<string, number>>({})

  function renderModal(node: React.ReactNode) {
    if (typeof document === 'undefined') return null
    return createPortal(node, document.body)
  }

  function isPosting(key: string) {
    return postStateByKey[key] === 'loading'
  }

  function buttonLabel(key: string, idle: string, loading = 'Saving...', success = 'Saved') {
    if (postStateByKey[key] === 'loading') return loading
    if (postStateByKey[key] === 'success') return success
    return idle
  }

  function setPostLoading(key: string) {
    if (postResetTimers.current[key]) {
      window.clearTimeout(postResetTimers.current[key])
      delete postResetTimers.current[key]
    }
    setPostStateByKey(prev => ({ ...prev, [key]: 'loading' }))
  }

  function setPostIdle(key: string) {
    if (postResetTimers.current[key]) {
      window.clearTimeout(postResetTimers.current[key])
      delete postResetTimers.current[key]
    }
    setPostStateByKey(prev => ({ ...prev, [key]: 'idle' }))
  }

  function setPostSuccess(key: string) {
    if (postResetTimers.current[key]) window.clearTimeout(postResetTimers.current[key])
    setPostStateByKey(prev => ({ ...prev, [key]: 'success' }))
    postResetTimers.current[key] = window.setTimeout(() => {
      setPostStateByKey(prev => ({ ...prev, [key]: 'idle' }))
      delete postResetTimers.current[key]
    }, 1500)
  }

  function armConfirm(key: string, value: number, setter: (value: number | null) => void) {
    if (confirmResetTimers.current[key]) window.clearTimeout(confirmResetTimers.current[key])
    setter(value)
    confirmResetTimers.current[key] = window.setTimeout(() => {
      setter(null)
      delete confirmResetTimers.current[key]
    }, 3000)
  }

  function clearConfirm(key: string, setter: (value: number | null) => void) {
    if (confirmResetTimers.current[key]) {
      window.clearTimeout(confirmResetTimers.current[key])
      delete confirmResetTimers.current[key]
    }
    setter(null)
  }

  useEffect(() => {
    return () => {
      for (const timer of Object.values(postResetTimers.current)) window.clearTimeout(timer)
      for (const timer of Object.values(confirmResetTimers.current)) window.clearTimeout(timer)
      activeZipCancelRef.current?.()
    }
  }, [])

  const anyModalOpen = judgeDialogOpen || judgeExportDialogOpen || deadlineDialogOpen || registrationDialogOpen || entriesDialogOpen

  useEffect(() => {
    if (typeof document === 'undefined') return
    const { body } = document
    const previousStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width
    }

    if (anyModalOpen) {
      bodyScrollYRef.current = window.scrollY
      body.style.overflow = 'hidden'
      body.style.position = 'fixed'
      body.style.top = `-${bodyScrollYRef.current}px`
      body.style.width = '100%'
    }

    return () => {
      body.style.overflow = previousStyles.overflow
      body.style.position = previousStyles.position
      body.style.top = previousStyles.top
      body.style.width = previousStyles.width

      if (anyModalOpen) {
        window.scrollTo(0, bodyScrollYRef.current)
      }
    }
  }, [anyModalOpen])

  function showToast(message: string, type: 'success'|'error' = 'success') {
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current)
    setToast({ message, type })
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null)
      toastTimerRef.current = null
    }, 3000)
  }

  async function copyCompetitionLink(slug: string) {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/c/${slug}`)
      showToast('Link copied to clipboard', 'success')
    } catch {
      showToast('Could not copy link.', 'error')
    }
  }

  async function load() {
    setErr('')
    clearConfirm('close-submissions', setCloseSubmissionsConfirmId)
    clearConfirm('close-judging', setCloseJudgingConfirmId)
    try {
      setMe(await api('/api/admin/me'))
      const c = await api<{competitions: Competition[]}>('/api/admin/competitions')
      setComps(c.competitions)
    } catch (e:any) { setErr(e.message) }
  }
  useEffect(()=>{ load() }, [])

  async function login() {
    setErr(''); setOk(''); setCreatedSlug('')
    setPostLoading('login')
    try {
      await Promise.all([
        api('/api/admin/login', { method:'POST', headers:{'content-type':'application/json'}, body: JSON.stringify({ username, password }) }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      await load()
      setPostSuccess('login')
    } catch (e:any) {
      setErr(e.message)
      setPostIdle('login')
    }
  }
  async function logout() {
    setPostLoading('logout')
    try {
      await Promise.all([
        api('/api/admin/logout', { method:'POST' }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      await load()
      setPostSuccess('logout')
    } catch (e:any) {
      setErr(e.message || 'Could not log out.')
      setPostIdle('logout')
    }
  }

  async function createCompetition() {
    setErr(''); setOk(''); setCreatedSlug('')
    if (!title.trim()) {
      setErr('Title is required.')
      return
    }
    if (!isValidSlug(slug)) {
      setErr('Link name must use lowercase letters, numbers, and dashes only.')
      return
    }
    if (!closesAt || Number.isNaN(new Date(closesAt).getTime())) {
      setErr('Please choose a valid closing date and time.')
      return
    }
    setPostLoading('create-competition')
    try {
      await Promise.all([
        api('/api/admin/competitions', { method:'POST', headers:{'content-type':'application/json'}, body: JSON.stringify({ title, slug, closesAt: new Date(closesAt).toISOString() }) }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      setOk(`Competition "${title.trim()}" created successfully.`)
      setCreatedSlug(slug.trim())
      setTitle('')
      setSlug('')
      setSlugIsManual(false)
      setClosesAt(defaultCloseAtOneMonthAheadMidnight())
      await load()
      setPostSuccess('create-competition')
    } catch (e:any) {
      setErr(e.message)
      setPostIdle('create-competition')
    }
  }

  async function closeJudging(id:number){
    setErr('')
    const key = `close-judging-${id}`
    setPostLoading(key)
    try {
      await Promise.all([
        api(`/api/admin/competitions/${id}/close-judging`, { method:'POST' }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      clearConfirm('close-judging', setCloseJudgingConfirmId)
      showToast('Judging is now closed.', 'success')
      await load()
      setPostSuccess(key)
    } catch (e:any) {
      showToast(e.message || 'Could not close judging.', 'error')
      setPostIdle(key)
    }
  }
  async function closeSubmissions(id:number){
    setErr('')
    const key = `close-submissions-${id}`
    setPostLoading(key)
    try {
      await Promise.all([
        api(`/api/admin/competitions/${id}/close-submissions`, { method:'POST' }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      clearConfirm('close-submissions', setCloseSubmissionsConfirmId)
      showToast('Submissions are now closed.', 'success')
      await load()
      setPostSuccess(key)
    } catch(e:any){
      showToast(e.message || 'Could not close submissions.', 'error')
      setPostIdle(key)
    }
  }
  async function reopenSubmissions(id:number){
    setErr('')
    const key = `reopen-submissions-${id}`
    setPostLoading(key)
    try {
      await Promise.all([
        api(`/api/admin/competitions/${id}/reopen-submissions`, { method:'POST' }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      showToast('Submissions are now open again.', 'success')
      await load()
      setPostSuccess(key)
    } catch(e:any){
      showToast(e.message || 'Could not reopen submissions.', 'error')
      setPostIdle(key)
    }
  }
  function requestCloseSubmissions(id:number) {
    if (closeSubmissionsConfirmId === id) {
      void closeSubmissions(id)
      return
    }
    armConfirm('close-submissions', id, setCloseSubmissionsConfirmId)
    showToast('Press "Close submissions" again to confirm.', 'error')
  }
  function requestCloseJudging(id:number) {
    if (closeJudgingConfirmId === id) {
      void closeJudging(id)
      return
    }
    armConfirm('close-judging', id, setCloseJudgingConfirmId)
    showToast('Press "Close judging" again to confirm.', 'error')
  }
  function openJudgeDialog(id:number) {
    setJudgeCompId(id)
    setJudgeName('')
    setJudgeDialogOpen(true)
  }
  function closeJudgeDialog() {
    setJudgeDialogOpen(false)
    setJudgeName('')
    setJudgeCompId(null)
  }
  async function openJudgeExportDialog(id:number, titleText:string) {
    setErr('')
    setJudgeExportDialogOpen(true)
    setJudgeExportCompId(id)
    setJudgeExportCompTitle(titleText)
    setJudgeExportJudges([])
    setSelectedJudgeExportId('')
    setJudgeExportLoading(true)
    try {
      const d = await api<JudgeExportResp>(`/api/admin/competitions/${id}/judges`)
      setJudgeExportJudges(d.judges || [])
      if ((d.judges || []).length > 0) setSelectedJudgeExportId(String(d.judges[0].id))
    } catch (e:any) {
      setErr(e.message || 'Could not load judges.')
      setJudgeExportDialogOpen(false)
      setJudgeExportCompId(null)
      setJudgeExportCompTitle('')
    } finally {
      setJudgeExportLoading(false)
    }
  }
  function closeJudgeExportDialog() {
    setJudgeExportDialogOpen(false)
    setJudgeExportCompId(null)
    setJudgeExportCompTitle('')
    setJudgeExportJudges([])
    setSelectedJudgeExportId('')
    setJudgeExportLoading(false)
  }
  function openDeadlineDialog(comp: Competition) {
    setDeadlineCompId(comp.id)
    setDeadlineCompTitle(comp.title)
    setDeadlineValue(toLocalDateTimeInputValue(new Date(comp.closesAt)))
    setDeadlineDialogOpen(true)
  }
  function closeDeadlineDialog() {
    setDeadlineDialogOpen(false)
    setDeadlineCompId(null)
    setDeadlineCompTitle('')
    setDeadlineValue('')
  }
  function openRegistrationDialog(comp: Competition) {
    setRegistrationDialogOpen(true)
    setRegistrationCompId(comp.id)
    setRegistrationCompTitle(comp.title)
    setRegistrationCompetitionNumber('')
    setRegistrationEmailValue('')
    setRegistrationResult(null)
    clearConfirm('reset-registration', setResetRegistrationConfirmId)
  }
  function closeRegistrationDialog() {
    setRegistrationDialogOpen(false)
    setRegistrationCompId(null)
    setRegistrationCompTitle('')
    setRegistrationCompetitionNumber('')
    setRegistrationEmailValue('')
    setRegistrationResult(null)
    clearConfirm('reset-registration', setResetRegistrationConfirmId)
  }
  async function lookupRegistration() {
    setErr('')
    if (!registrationCompId) return
    if (!isValidCompetitionNumber(registrationCompetitionNumber)) {
      showToast('Competition number must start with N, I, or A and contain digits only.', 'error')
      return
    }
    const competitionNumber = registrationCompetitionNumber.trim().toUpperCase()
    const key = `lookup-registration-${registrationCompId}`
    setPostLoading(key)
    clearConfirm('reset-registration', setResetRegistrationConfirmId)
    try {
      const [d] = await Promise.all([
        api<AdminSubmissionResp>(`/api/admin/competitions/${registrationCompId}/submissions/${encodeURIComponent(competitionNumber)}`),
        new Promise(resolve => setTimeout(resolve, 300))
      ])
      setRegistrationCompetitionNumber(d.submission.competitionNumber)
      setRegistrationEmailValue(d.submission.email)
      setRegistrationResult(d.submission)
      setPostSuccess(key)
    } catch (e:any) {
      setRegistrationResult(null)
      setRegistrationEmailValue('')
      showToast(e.message || 'Could not find registration.', 'error')
      setPostIdle(key)
    }
  }
  async function changeRegistrationEmail() {
    setErr('')
    if (!registrationCompId || !registrationResult) return
    if (!isValidEmail(registrationEmailValue)) {
      showToast('Please enter a valid email address.', 'error')
      return
    }
    const key = `change-registration-email-${registrationResult.id}`
    setPostLoading(key)
    try {
      const [d] = await Promise.all([
        api<AdminSubmissionResp>(`/api/admin/competitions/${registrationCompId}/submissions/${encodeURIComponent(registrationResult.competitionNumber)}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email: registrationEmailValue.trim() })
        }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      setRegistrationResult(d.submission)
      setRegistrationEmailValue(d.submission.email)
      clearConfirm('reset-registration', setResetRegistrationConfirmId)
      showToast('Registration email updated.', 'success')
      setPostSuccess(key)
    } catch (e:any) {
      showToast(e.message || 'Could not update registration email.', 'error')
      setPostIdle(key)
    }
  }
  async function resetRegistration() {
    setErr('')
    if (!registrationCompId || !registrationResult) return
    const key = `reset-registration-${registrationResult.id}`
    setPostLoading(key)
    try {
      await Promise.all([
        api(`/api/admin/competitions/${registrationCompId}/submissions/${encodeURIComponent(registrationResult.competitionNumber)}`, { method: 'DELETE' }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      showToast('Registration reset.', 'success')
      setRegistrationResult(null)
      setRegistrationEmailValue('')
      setRegistrationCompetitionNumber('')
      clearConfirm('reset-registration', setResetRegistrationConfirmId)
      setPostSuccess(key)
    } catch (e:any) {
      showToast(e.message || 'Could not reset registration.', 'error')
      setPostIdle(key)
    }
  }
  function requestResetRegistration() {
    if (!registrationResult) return
    if (resetRegistrationConfirmId === registrationResult.id) {
      void resetRegistration()
      return
    }
    armConfirm('reset-registration', registrationResult.id, setResetRegistrationConfirmId)
    showToast('Press "Reset registration" again to confirm.', 'error')
  }
  async function saveDeadline() {
    setErr('')
    if (!deadlineCompId) return
    if (!deadlineValue || Number.isNaN(new Date(deadlineValue).getTime())) {
      setErr('Please choose a valid closing date and time.')
      return
    }
    const key = `update-deadline-${deadlineCompId}`
    setPostLoading(key)
    try {
      await Promise.all([
        api(`/api/admin/competitions/${deadlineCompId}/deadline`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ closesAt: new Date(deadlineValue).toISOString() })
        }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      showToast('Submission deadline updated.', 'success')
      closeDeadlineDialog()
      await load()
      setPostSuccess(key)
    } catch (e:any) {
      showToast(e.message || 'Could not update submission deadline.', 'error')
      setPostIdle(key)
    }
  }
  function downloadJudgeExport() {
    if (!judgeExportCompId || !selectedJudgeExportId) return
    const url = `/api/admin/competitions/${judgeExportCompId}/judges/${selectedJudgeExportId}.xlsx`
    window.open(url, '_blank', 'noopener,noreferrer')
  }
  async function downloadCompetitionZip(comp: Competition) {
    setErr('')
    const key = `download-zip-${comp.id}`
    setPostLoading(key)
    try {
      const data = await api<AdminEntriesResp>(`/api/admin/competitions/${comp.id}/entries`)
      if (data.entries.length === 0) throw new Error('No uploaded entries are available for this competition.')
      const task = startZipDownloadFromWorker(
        data.entries.map((entry) => ({ name: entry.zipPath, url: entry.previewUrl })),
        `${data.competition.slug}.zip`,
        (progress) => setZipDialog((prev) => prev ? { ...prev, progress } : prev)
      )
      activeZipCancelRef.current = task.cancel
      setZipDialog({
        title: data.competition.title,
        progress: {
          phase: 'downloading',
          completed: 0,
          total: data.entries.length
        },
        cancel: task.cancel
      })
      await task.done
      setPostSuccess(key)
    } catch (e:any) {
      if (!(e instanceof ZipDownloadCancelledError)) setErr(e.message || 'Could not prepare ZIP download.')
      setPostIdle(key)
    } finally {
      activeZipCancelRef.current = null
      setZipDialog(null)
    }
  }
  async function createJudge() {
    setErr(''); setOk(''); setCreatedSlug('')
    if (!judgeCompId) return
    if (!judgeName.trim()) {
      setErr('Please enter a judge name.')
      return
    }
    setPostLoading('create-judge')
    try {
      const [d] = await Promise.all([
        api<{judgeUrl:string}>(`/api/admin/competitions/${judgeCompId}/judges`, {
          method:'POST',
          headers:{'content-type':'application/json'},
          body: JSON.stringify({ name: judgeName.trim() })
        }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      showToast('Judge link created.', 'success')
      try {
        await navigator.clipboard.writeText(d.judgeUrl)
        showToast('Judge link copied to clipboard', 'success')
      } catch {
        showToast('Judge link created, but clipboard access was blocked.', 'error')
      }
      closeJudgeDialog()
      setPostSuccess('create-judge')
    } catch (e:any) {
      setErr(e.message)
      setPostIdle('create-judge')
    }
  }
  async function openEntriesDialog(id:number) {
    setErr('')
    setEntriesDialogOpen(true)
    setEntriesLoading(true)
    setEntriesData(null)
    setEntriesColumns(defaultEntriesColumns())
    setEntriesActiveClass('')
    setEntriesActiveCategory('')
    try {
      const d = await api<AdminEntriesResp>(`/api/admin/competitions/${id}/entries`)
      setEntriesData(d)
    } catch (e:any) {
      setErr(e.message)
      setEntriesDialogOpen(false)
    } finally {
      setEntriesLoading(false)
    }
  }
  function closeEntriesDialog() {
    setEntriesDialogOpen(false)
    setEntriesLoading(false)
    setEntriesData(null)
    setEntriesActiveClass('')
    setEntriesActiveCategory('')
    clearConfirm('delete-entry', setDeleteEntryConfirmId)
  }
  async function deleteEntry(entryId:number) {
    if (!entriesData) return
    const competitionId = entriesData.competition.id
    const key = `delete-entry-${entryId}`
    setPostLoading(key)
    try {
      await Promise.all([
        api(`/api/admin/competitions/${competitionId}/entries/${entryId}`, { method:'DELETE' }),
        new Promise(resolve => setTimeout(resolve, 450))
      ])
      setEntriesData(prev => prev ? ({
        ...prev,
        entries: prev.entries.filter(entry => entry.id !== entryId)
      }) : prev)
      clearConfirm('delete-entry', setDeleteEntryConfirmId)
      showToast('Entry deleted.', 'success')
      setPostSuccess(key)
    } catch (e:any) {
      showToast(e.message || 'Could not delete entry.', 'error')
      setPostIdle(key)
    }
  }
  function requestDeleteEntry(entryId:number) {
    if (deleteEntryConfirmId === entryId) {
      void deleteEntry(entryId)
      return
    }
    armConfirm('delete-entry', entryId, setDeleteEntryConfirmId)
    showToast('Press "Delete entry" again to confirm.', 'error')
  }

  const entriesClassOrder: Record<string, number> = { Novice: 0, Intermediate: 1, Advanced: 2 }
  const entriesCategoryOrder: Record<string, number> = { Colour: 0, Mono: 1, Themed: 2 }
  const entryClasses = entriesData
    ? Array.from(new Set(entriesData.entries.map(entry => entry.className))).sort(
        (a, b) => (entriesClassOrder[a] ?? 99) - (entriesClassOrder[b] ?? 99)
      )
    : []
  const activeEntriesClass = entryClasses.includes(entriesActiveClass) ? entriesActiveClass : (entryClasses[0] || '')
  const availableCategories = activeEntriesClass
    ? ENTRY_CATEGORY_TABS.filter(category =>
        entriesData?.entries.some(entry => entry.className === activeEntriesClass && entry.categoryLabel === category)
      )
    : []
  const activeEntriesCategory = availableCategories.includes(entriesActiveCategory as typeof ENTRY_CATEGORY_TABS[number])
    ? entriesActiveCategory
    : (availableCategories[0] || '')
  const visibleEntries = entriesData
    ? entriesData.entries.filter(entry => entry.className === activeEntriesClass && entry.categoryLabel === activeEntriesCategory)
    : []
  const deadlinePostKey = deadlineCompId ? `update-deadline-${deadlineCompId}` : 'update-deadline'

  return (
    <div className="card">
      {toast && <div className={`toast ${toast.type}`}>{toast.message}</div>}
      <h2 style={{marginTop:0}}>Manage Competitions</h2>
      {err && <p className='error-text'>{err}</p>}
      {ok && (
        <div className="success-text">
          <div><b>{ok}</b></div>
          {createdSlug && (
            <div style={{marginTop:6}}>
              <a href={`/c/${createdSlug}`} target="_blank" rel="noreferrer">Open submission page</a>
            </div>
          )}
        </div>
      )}

      {!me.loggedIn ? (
        <>
          <form onSubmit={(e) => { e.preventDefault(); void login() }}>
            <div className="grid2">
              <div><label>Username</label><input value={username} onChange={e=>setUsername(e.target.value)} /></div>
              <div><label>Password</label><input value={password} onChange={e=>setPassword(e.target.value)} type="password" /></div>
            </div>
            <div className="row" style={{marginTop:12}}>
              <button className="primary" type="submit" disabled={isPosting('login')}>
                {isPosting('login') ? (
                  <span className="row" style={{gap:8}}><span className="btn-spinner" /> <span>{buttonLabel('login', 'Login', 'Signing in...', 'Done')}</span></span>
                ) : buttonLabel('login', 'Login', 'Signing in...', 'Done')}
              </button>
            </div>
          </form>
        </>
      ) : (
        <>
          <div className="row" style={{justifyContent:'space-between'}}>
            <span className="badge">logged in: {me.username}</span>
            <div className="row">
              <button onClick={logout} disabled={isPosting('logout')}>
                {isPosting('logout') ? (
                  <span className="row" style={{gap:8}}><span className="btn-spinner" /> <span>{buttonLabel('logout', 'Logout', 'Signing out...', 'Done')}</span></span>
                ) : buttonLabel('logout', 'Logout', 'Signing out...', 'Done')}
              </button>
            </div>
          </div>

          <hr />

          <h3 style={{marginTop:0}}>Create competition</h3>
          <div className="grid2">
            <div>
              <label>Title</label>
              <input
                value={title}
                onChange={e => {
                  const nextTitle = e.target.value
                  setTitle(nextTitle)
                  if (!slugIsManual) setSlug(makeSlug(nextTitle))
                }}
              />
            </div>
            <div>
              <label>Link name</label>
              <input
                value={slug}
                onChange={e => {
                  setSlug(e.target.value)
                  setSlugIsManual(true)
                }}
                placeholder="monthly-competition"
              />
              <div className="row" style={{marginTop:6}}>
                <small className="muted">
                  {slugIsManual ? 'Using custom link name.' : 'Auto-filled from title.'}
                </small>
                {slugIsManual && (
                  <button
                    type="button"
                    onClick={() => {
                      setSlug(makeSlug(title))
                      setSlugIsManual(false)
                    }}
                  >
                    Use auto-fill
                  </button>
                )}
              </div>
            </div>
          </div>
          <div style={{marginTop:12}}>
            <label>Closes at</label>
            <input type="datetime-local" value={closesAt} onChange={e=>setClosesAt(e.target.value)} />
          </div>
          <div className="row" style={{marginTop:12}}>
            <button className="primary" onClick={createCompetition} disabled={isPosting('create-competition')}>
              {isPosting('create-competition') ? (
                <span className="row" style={{gap:8}}><span className="btn-spinner" /> <span>{buttonLabel('create-competition', 'Create', 'Creating...', 'Saved')}</span></span>
              ) : buttonLabel('create-competition', 'Create', 'Creating...', 'Saved')}
            </button>
          </div>

          <hr />
          <h3 style={{marginTop:0}}>Competitions</h3>
          <div style={{display:'grid', gap: 12}}>
            {comps.map(c => (
              (() => {
                const beforeCloseTime = Date.now() < new Date(c.closesAt).getTime()
                const showReopen = c.status === 'submissions_closed' && beforeCloseTime
                const judgingIsAvailable = c.status === 'submissions_closed' || !beforeCloseTime
                return (
              <div key={c.id} className="card" style={{background:'#0b1220'}}>
                <div className="row" style={{justifyContent:'space-between'}}>
                  <div>
                    <div style={{fontWeight:700}}>{c.title}</div>
                    <div>
                      <small className="muted">
                        <button
                          type="button"
                          className="text-link-button"
                          onClick={() => copyCompetitionLink(c.slug)}
                          title="Copy competition link"
                        >
                          /{c.slug}
                        </button>
                        {' '}—{' '}
                        <button
                          type="button"
                          className="deadline-trigger-button"
                          onClick={() => openDeadlineDialog(c)}
                          title="Change submission deadline"
                        >
                          closes {new Date(c.closesAt).toLocaleString()}
                        </button>
                      </small>
                    </div>
                    <div className="row" style={{marginTop:6}}>
                      <span className="badge">{competitionStatusLabel(c.status)}</span>
                    </div>
                  </div>
                  <div className="row" style={{marginLeft:'auto'}}>
                    <button onClick={()=>openEntriesDialog(c.id)}>View entries</button>
                    <button onClick={()=>openRegistrationDialog(c)}>Find registration</button>
                    {showReopen ? (
                      <button onClick={()=>reopenSubmissions(c.id)} disabled={isPosting(`reopen-submissions-${c.id}`)}>
                        {isPosting(`reopen-submissions-${c.id}`) ? (
                          <span className="row" style={{gap:8}}><span className="btn-spinner" /> <span>{buttonLabel(`reopen-submissions-${c.id}`, 'Re-open submissions', 'Saving...', 'Saved')}</span></span>
                        ) : buttonLabel(`reopen-submissions-${c.id}`, 'Re-open submissions', 'Saving...', 'Saved')}
                      </button>
                    ) : (
                      <button onClick={()=>requestCloseSubmissions(c.id)} disabled={isPosting(`close-submissions-${c.id}`)}>
                        {isPosting(`close-submissions-${c.id}`) ? (
                          <span className="row" style={{gap:8}}><span className="btn-spinner" /> <span>{buttonLabel(`close-submissions-${c.id}`, 'Close submissions', 'Saving...', 'Saved')}</span></span>
                        ) : closeSubmissionsConfirmId === c.id ? 'Confirm close submissions' : buttonLabel(`close-submissions-${c.id}`, 'Close submissions', 'Saving...', 'Saved')}
                      </button>
                    )}
                    <button onClick={()=>openJudgeDialog(c.id)}>Create judge link</button>
                    <button onClick={()=>openJudgeExportDialog(c.id, c.title)}>Judge export</button>
                    <button
                      onClick={()=>requestCloseJudging(c.id)}
                      disabled={!judgingIsAvailable || c.status === 'judging_closed' || isPosting(`close-judging-${c.id}`)}
                      title={
                        c.status === 'judging_closed'
                          ? 'Judging is already closed.'
                          : !judgingIsAvailable
                            ? 'Judging has not been opened yet.'
                            : undefined
                      }
                    >
                      {isPosting(`close-judging-${c.id}`) ? (
                        <span className="row" style={{gap:8}}><span className="btn-spinner" /> <span>{buttonLabel(`close-judging-${c.id}`, 'Close judging', 'Saving...', 'Saved')}</span></span>
                      ) : closeJudgingConfirmId === c.id ? 'Confirm close judging' : buttonLabel(`close-judging-${c.id}`, 'Close judging', 'Saving...', 'Saved')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { void downloadCompetitionZip(c) }}
                      className="icon-button"
                      aria-label="Download ZIP"
                      title="Download ZIP"
                      disabled={isPosting(`download-zip-${c.id}`)}
                    >
                      {isPosting(`download-zip-${c.id}`) ? (
                        <span className="btn-spinner" />
                      ) : (
                        <span className="zip-icon" aria-hidden="true">
                          <span className="zip-icon-tab" />
                          <span className="zip-icon-body" />
                          <span className="zip-icon-zipper" />
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
                )
              })()
            ))}
          </div>
        </>
      )}
      {judgeDialogOpen && renderModal(
        <div className="modal-backdrop" onClick={closeJudgeDialog}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3 style={{marginTop:0}}>Create judge link</h3>
            <label>Judge name</label>
            <input
              value={judgeName}
              onChange={e=>setJudgeName(e.target.value)}
              placeholder="Judge 1"
              autoFocus
            />
            <div className="row" style={{marginTop:12}}>
              <button className="primary" onClick={createJudge} disabled={isPosting('create-judge')}>
                {isPosting('create-judge') ? (
                  <span className="row" style={{gap:8}}><span className="btn-spinner" /> <span>{buttonLabel('create-judge', 'Create and copy link', 'Creating...', 'Saved')}</span></span>
                ) : buttonLabel('create-judge', 'Create and copy link', 'Creating...', 'Saved')}
              </button>
              <button type="button" onClick={closeJudgeDialog}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {judgeExportDialogOpen && renderModal(
        <div className="modal-backdrop" onClick={closeJudgeExportDialog}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3 style={{marginTop:0}}>Judge Excel export</h3>
            <div><small className="muted">{judgeExportCompTitle}</small></div>
            {judgeExportLoading ? (
              <p style={{marginTop:12}}><small className="muted">Loading judges…</small></p>
            ) : judgeExportJudges.length === 0 ? (
              <p style={{marginTop:12}}><small className="muted">No judge has submitted scores for this competition yet.</small></p>
            ) : (
              <>
                <div style={{marginTop:12}}>
                  <label>Judge</label>
                  <select
                    value={selectedJudgeExportId}
                    onChange={e => setSelectedJudgeExportId(e.target.value)}
                    style={{width:'100%'}}
                  >
                    {judgeExportJudges.map(j => (
                      <option key={j.id} value={String(j.id)}>
                        {j.name} ({j.scoreCount} scored)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="row" style={{marginTop:12}}>
                  <button className="primary" type="button" onClick={downloadJudgeExport} disabled={!selectedJudgeExportId}>
                    Download Excel
                  </button>
                </div>
              </>
            )}
            <div className="row" style={{marginTop:12}}>
              <button type="button" onClick={closeJudgeExportDialog}>Close</button>
            </div>
          </div>
        </div>
      )}
      {deadlineDialogOpen && renderModal(
        <div className="modal-backdrop" onClick={closeDeadlineDialog}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3 style={{marginTop:0}}>Change submission deadline</h3>
            <div><small className="muted">{deadlineCompTitle}</small></div>
            <div style={{marginTop:12}}>
              <label>Closes at</label>
              <input
                type="datetime-local"
                value={deadlineValue}
                onChange={e => setDeadlineValue(e.target.value)}
                autoFocus
              />
            </div>
            <div className="row" style={{marginTop:12}}>
              <button className="primary" type="button" onClick={saveDeadline} disabled={!deadlineCompId || isPosting(deadlinePostKey)}>
                {isPosting(deadlinePostKey) ? (
                  <span className="row" style={{gap:8}}><span className="btn-spinner" /> <span>{buttonLabel(deadlinePostKey, 'Save deadline', 'Saving...', 'Saved')}</span></span>
                ) : buttonLabel(deadlinePostKey, 'Save deadline', 'Saving...', 'Saved')}
              </button>
              <button type="button" onClick={closeDeadlineDialog}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {registrationDialogOpen && renderModal(
        <div className="modal-backdrop" onClick={closeRegistrationDialog}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3 style={{marginTop:0}}>Find registration</h3>
            <div><small className="muted">{registrationCompTitle}</small></div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void lookupRegistration()
              }}
              style={{marginTop:12}}
            >
              <label>Competition number</label>
              <div className="row registration-lookup-row">
                <input
                  value={registrationCompetitionNumber}
                  onChange={e => {
                    setRegistrationCompetitionNumber(e.target.value.toUpperCase())
                    setRegistrationResult(null)
                    setRegistrationEmailValue('')
                    clearConfirm('reset-registration', setResetRegistrationConfirmId)
                  }}
                  placeholder="I394"
                  autoFocus
                />
                <button
                  className="primary"
                  type="submit"
                  disabled={!registrationCompId || isPosting(`lookup-registration-${registrationCompId || 0}`)}
                >
                  {isPosting(`lookup-registration-${registrationCompId || 0}`) ? (
                    <span className="row" style={{gap:8}}><span className="btn-spinner" /> <span>{buttonLabel(`lookup-registration-${registrationCompId || 0}`, 'Look up', 'Looking...', 'Done')}</span></span>
                  ) : buttonLabel(`lookup-registration-${registrationCompId || 0}`, 'Look up', 'Looking...', 'Done')}
                </button>
              </div>
            </form>

            {registrationResult && (
              <div className="registration-result">
                <div className="row">
                  <span className="badge">{registrationResult.competitionNumber}</span>
                  <span className="badge">{registrationResult.className}</span>
                  <span className="badge">{registrationResult.entryCount} uploaded</span>
                </div>
                <div style={{marginTop:12}}>
                  <label>Email</label>
                  <input
                    value={registrationEmailValue}
                    onChange={e => setRegistrationEmailValue(e.target.value)}
                    type="email"
                  />
                </div>
                <div className="row" style={{marginTop:12}}>
                  <button
                    className="primary"
                    type="button"
                    onClick={changeRegistrationEmail}
                    disabled={isPosting(`change-registration-email-${registrationResult.id}`)}
                  >
                    {isPosting(`change-registration-email-${registrationResult.id}`) ? (
                      <span className="row" style={{gap:8}}><span className="btn-spinner" /> <span>{buttonLabel(`change-registration-email-${registrationResult.id}`, 'Change email', 'Saving...', 'Saved')}</span></span>
                    ) : buttonLabel(`change-registration-email-${registrationResult.id}`, 'Change email', 'Saving...', 'Saved')}
                  </button>
                  <button
                    className="danger"
                    type="button"
                    onClick={requestResetRegistration}
                    disabled={!registrationResult.canResetRegistration || isPosting(`reset-registration-${registrationResult.id}`)}
                    title={registrationResult.canResetRegistration ? undefined : 'This registration has uploaded entries.'}
                  >
                    {isPosting(`reset-registration-${registrationResult.id}`) ? (
                      <span className="row" style={{gap:8}}><span className="btn-spinner" /> <span>{buttonLabel(`reset-registration-${registrationResult.id}`, 'Reset registration', 'Resetting...', 'Reset')}</span></span>
                    ) : resetRegistrationConfirmId === registrationResult.id ? 'Confirm reset registration' : buttonLabel(`reset-registration-${registrationResult.id}`, 'Reset registration', 'Resetting...', 'Reset')}
                  </button>
                </div>
                {!registrationResult.canResetRegistration && (
                  <p className="error-text" style={{marginBottom:0}}>
                    This registration has uploaded entries. Change the email instead.
                  </p>
                )}
              </div>
            )}

            <div className="row" style={{marginTop:12}}>
              <button type="button" onClick={closeRegistrationDialog}>Close</button>
            </div>
          </div>
        </div>
      )}
      {entriesDialogOpen && renderModal(
        <div className="modal-backdrop" onClick={closeEntriesDialog}>
          <div className="modal-card modal-card-lg entries-modal-card" onClick={e => e.stopPropagation()}>
            <div className="row" style={{justifyContent:'space-between'}}>
              <h3 style={{marginTop:0, marginBottom:0}}>
                {entriesData ? `Entries: ${entriesData.competition.title}` : 'Entries'}
              </h3>
              <button
                type="button"
                className="icon-button modal-close-button"
                onClick={closeEntriesDialog}
                aria-label="Close entries dialog"
                title="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="entries-modal-body">
              {entriesLoading && <p><small className="muted">Loading entries…</small></p>}
              {!entriesLoading && entriesData && entriesData.entries.length === 0 && (
                <p><small className="muted">No uploaded entries yet.</small></p>
              )}
              {!entriesLoading && entriesData && entriesData.entries.length > 0 && (
                <>
                  <div className="entries-modal-controls">
                    <div>
                      <label>Class</label>
                      <select
                        value={activeEntriesClass}
                        onChange={(ev) => setEntriesActiveClass(ev.target.value)}
                        style={{minWidth:200, width:'50%'}}
                      >
                        {entryClasses.map(className => (
                          <option key={className} value={className}>{className}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label>Images across</label>
                      <select
                        value={entriesColumns}
                        onChange={(ev) => setEntriesColumns(Number(ev.target.value))}
                        style={{minWidth:80, width:'100%'}}
                      >
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                      </select>
                    </div>
                  </div>
                  <div className="entries-modal-category-row">
                    <div className="entries-modal-category-tabs">
                      {ENTRY_CATEGORY_TABS.map(category => {
                        const isAvailable = availableCategories.includes(category)
                        return (
                          <button
                            key={category}
                            type="button"
                            className={category === activeEntriesCategory ? 'primary' : undefined}
                            onClick={() => setEntriesActiveCategory(category)}
                            disabled={!isAvailable}
                          >
                            {category}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                  <div
                    className="entries-modal-grid"
                    style={{gridTemplateColumns: `repeat(${entriesColumns}, minmax(0, 1fr))`}}
                  >
                    {visibleEntries.map(entry => (
                      <div key={entry.id} className="card" style={{background:'#0b1220'}}>
                        <div style={{display:'grid', gridTemplateColumns:'minmax(0, 1fr) auto', gap:12, alignItems:'start'}}>
                          <div style={{minWidth:0}}>
                            <div style={{fontWeight:700}}>{entry.competitionNumber}</div>
                            <div><small className="muted">{entry.title}</small></div>
                          </div>
                          <button
                            type="button"
                            className={`icon-button danger-icon-button${deleteEntryConfirmId === entry.id ? ' danger-icon-button-armed' : ''}`}
                            onClick={() => requestDeleteEntry(entry.id)}
                            disabled={isPosting(`delete-entry-${entry.id}`)}
                            aria-label={deleteEntryConfirmId === entry.id ? 'Confirm delete entry' : 'Delete entry'}
                            title={deleteEntryConfirmId === entry.id ? 'Confirm delete entry' : 'Delete entry'}
                          >
                            {isPosting(`delete-entry-${entry.id}`) ? (
                              <span className="btn-spinner" />
                            ) : (
                              <span className="trash-icon" aria-hidden="true">
                                <span className="trash-icon-lid" />
                                <span className="trash-icon-body" />
                              </span>
                            )}
                          </button>
                        </div>
                        <div style={{marginTop:10}}>
                          <a href={entry.previewUrl} target="_blank" rel="noreferrer">
                            <img src={entry.previewUrl} alt={entry.title} style={{width:'100%', border:'1px solid rgba(255,255,255,0.45)'}} loading="lazy" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                  {visibleEntries.length === 0 && (
                    <p style={{marginTop:12}}><small className="muted">No uploaded entries in this class/category yet.</small></p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {zipDialog && (
        <ZipProgressDialog
          title={zipDialog.title}
          progress={zipDialog.progress}
          onCancel={zipDialog.cancel}
        />
      )}
    </div>
  )
}

export default function Admin() {
  return (
    <Routes>
      <Route path="/" element={<AdminHome />} />
      <Route path="*" element={<div className="card">Not found. <Link to="/admin">Back</Link></div>} />
    </Routes>
  )
}
